/**
 * Prepares a captured/uploaded bill photo for the rest of the app. Produces
 * TWO derived copies rather than mutating the original:
 *  - `original` — full resolution, used later for the actual IRD photo
 *    share/upload step (that form needs real photo quality). Re-encoded to
 *    JPEG ONLY if the source format isn't one IRD's form accepts (HEIC) —
 *    an already-JPEG/PNG/WebP file is passed through untouched, per "don't
 *    destroy the original image."
 *  - `forAnalysis` — a downscaled JPEG copy sent to Gemini, to keep upload
 *    size/latency/cost down. Never replaces or overwrites `original`.
 *
 * HEIC/HEIF support is best-effort: `createImageBitmap` decodes HEIC
 * natively on platforms whose OS/browser supports it (notably Safari/iOS —
 * the most common source of HEIC captures), with a clear error otherwise
 * rather than silently failing. Adding a dedicated HEIC-decode library was a
 * deliberate scope call — see docs/architecture.md.
 */
export interface PreparedImage {
  original: Blob;
  originalMimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  forAnalysis: Blob;
  /** Small copy for the History list thumbnail — kept separate so history storage stays light. */
  thumbnail: Blob;
  width: number;
  height: number;
}

export class ImageDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageDecodeError';
  }
}

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ANALYSIS_MAX_DIMENSION = 1600;
const ANALYSIS_JPEG_QUALITY = 0.85;
const THUMBNAIL_MAX_DIMENSION = 320;
const THUMBNAIL_JPEG_QUALITY = 0.7;

async function bitmapToBlob(bitmap: ImageBitmap, width: number, height: number, type: string, quality: number): Promise<Blob> {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageDecodeError('This browser does not support the image processing needed to prepare your photo.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas.convertToBlob({ type, quality });
}

export async function prepareBillImage(file: File): Promise<PreparedImage> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new ImageDecodeError(
      "We couldn't read this photo. If it's a HEIC/HEIF image, try switching your camera to a JPEG format in your phone's settings, or choose a different photo.",
    );
  }

  try {
    const sourceIsAccepted = ACCEPTED_TYPES.has(file.type);
    const original = sourceIsAccepted ? file : await bitmapToBlob(bitmap, bitmap.width, bitmap.height, 'image/jpeg', 0.95);
    const originalMimeType = (sourceIsAccepted ? file.type : 'image/jpeg') as PreparedImage['originalMimeType'];

    const analysisScale = Math.min(1, ANALYSIS_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const analysisWidth = Math.max(1, Math.round(bitmap.width * analysisScale));
    const analysisHeight = Math.max(1, Math.round(bitmap.height * analysisScale));
    const forAnalysis = await bitmapToBlob(bitmap, analysisWidth, analysisHeight, 'image/jpeg', ANALYSIS_JPEG_QUALITY);

    const thumbScale = Math.min(1, THUMBNAIL_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const thumbWidth = Math.max(1, Math.round(bitmap.width * thumbScale));
    const thumbHeight = Math.max(1, Math.round(bitmap.height * thumbScale));
    const thumbnail = await bitmapToBlob(bitmap, thumbWidth, thumbHeight, 'image/jpeg', THUMBNAIL_JPEG_QUALITY);

    return { original, originalMimeType, forAnalysis, thumbnail, width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}
